import { useEffect, useState } from "react";

import { Box, CircularProgress, Divider, LinearProgress, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import { alpha, useTheme } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import {
  analyzeKnowledgeBaseSync,
  applyKnowledgeBaseSync,
  loadKnowledgeBases,
} from "../../data/api/botpressKnowledgeBaseSync.js";
import Checkbox from "../ui/omniCheckbox";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogMessage from "../ui/omniDialogMessage";
import DialogTitle from "../ui/omniDialogTitle";
import OmniMenuItem from "../ui/omniMenuItem.js";
import OmniTextField from "../ui/omniTextField.js";

// Choose a sensible target KB for a given source KB: prefer the same kbId, then
// a target KB with a matching label (same website / same title), else the first
// target KB. Keeps the mapping intentional instead of order-dependent.
const pickDefaultTargetKb = (sourceKbId, sourceKbs, targetKbs) => {
  if (targetKbs.some(kb => kb.kbId === sourceKbId)) {
    return sourceKbId;
  }
  const sourceLabel = sourceKbs.find(kb => kb.kbId === sourceKbId)?.label;
  const labelMatch = sourceLabel && targetKbs.find(kb => kb.label === sourceLabel);
  if (labelMatch) {
    return labelMatch.kbId;
  }
  return targetKbs[0]?.kbId || sourceKbId;
};

// A scrollable, labelled list of file keys (one section of the preview).
const FileKeySection = ({ title, files, color }) => {
  const theme = useTheme();
  if (!files || files.length === 0) {
    return null;
  }
  return (
    <Box sx={{ marginTop: theme.spacing(2) }}>
      <Typography variant="subtitle2" sx={{ color }}>
        {title} ({files.length})
      </Typography>
      <Box
        sx={{
          marginTop: theme.spacing(0.5),
          padding: theme.spacing(1),
          borderRadius: theme.spacing(1),
          border: `1px solid ${theme.palette.divider}`,
          maxHeight: "22vh",
          overflowY: "auto",
        }}
      >
        {files.map(file => (
          <Typography
            key={file.targetKey}
            variant="body2"
            sx={{ wordBreak: "break-all", color: theme.palette.commonground.contrastText }}
          >
            {file.targetKey}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

// Preview-and-confirm dialog for migrating a single knowledge base from a source
// (Production) bot into a target (Test) bot. Steps: pick which KB to sync (and
// where it lands) -> analyze the diff -> confirm -> copy. Direction is fixed by
// the caller — this dialog never writes back to the source.
const BotpressKnowledgeBaseSyncDialog = props => {
  const {
    open,
    onClose,
    sourceBotODM,
    targetBotODM,
    dialogTitle = "Sync Knowledge Base (Prod → Test)",
    maxWidth = "sm",
    fullWidth = true,
  } = props;

  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // "loading-kbs" -> "select" -> "analyzing" -> "preview" -> "applying" -> "done" | "error"
  const [phase, setPhase] = useState("loading-kbs");
  const [statusText, setStatusText] = useState("Loading knowledge bases…");
  const [progressValue, setProgressValue] = useState(0);
  const [isDeterminate, setIsDeterminate] = useState(false);

  const [kbData, setKbData] = useState(null);
  const [sourceKbId, setSourceKbId] = useState("");
  const [targetKbId, setTargetKbId] = useState("");

  const [plan, setPlan] = useState(null);
  const [prune, setPrune] = useState(false);
  const [result, setResult] = useState(null);

  const sourceName = sourceBotODM?.omniData?.name || "Production";
  const targetName = targetBotODM?.omniData?.name || "Test";

  // Load both bots' KBs when the dialog opens, and pick sensible defaults: the
  // first source KB, mapped to the same-id target KB when one exists.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    setPhase("loading-kbs");
    setStatusText("Loading knowledge bases…");
    setIsDeterminate(false);
    setKbData(null);
    setPlan(null);
    setResult(null);
    setPrune(false);

    loadKnowledgeBases({ sourceBotODM, targetBotODM })
      .then(data => {
        if (cancelled) {
          return;
        }
        setKbData(data);
        const firstSource =
          data.sourceKbs.find(kb => kb.kbId === "kb-default") || data.sourceKbs[0];
        const initialSource = firstSource?.kbId || "";
        setSourceKbId(initialSource);
        setTargetKbId(pickDefaultTargetKb(initialSource, data.sourceKbs, data.targetKbs));
        setPhase("select");
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        console.error("Failed to load knowledge bases:", error);
        enqueueSnackbar(`Could not load knowledge bases: ${error.message}`, { variant: "error" });
        setStatusText(`Failed to load knowledge bases: ${error.message}`);
        setPhase("error");
      });

    return () => {
      cancelled = true;
    };
    // Disable eslint because the bot ODMs are stable for an open dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sourceKbs = kbData?.sourceKbs || [];
  const targetKbs = kbData?.targetKbs || [];
  const targetKbExists = targetKbs.some(kb => kb.kbId === targetKbId);

  // Target dropdown = every TEST KB, plus a "keep the source id" option when the
  // source KB has no same-id match in TEST (the warn-but-allow path).
  const targetOptions = [...targetKbs];
  if (sourceKbId && !targetKbs.some(kb => kb.kbId === sourceKbId)) {
    targetOptions.push({ kbId: sourceKbId, label: "same id — not in TEST yet", fileCount: 0 });
  }

  const handleAnalyzeProgress = event => {
    if (event.phase === "hashing") {
      setStatusText(`Comparing files (${event.index + 1} of ${event.total})…`);
    }
  };

  const handleAnalyze = () => {
    setPhase("analyzing");
    setStatusText("Comparing knowledge bases…");
    setIsDeterminate(false);

    analyzeKnowledgeBaseSync({
      sourceBotODM,
      targetBotODM,
      sourceFiles: kbData.sourceFiles,
      targetFiles: kbData.targetFiles,
      sourceKbId,
      targetKbId,
      onProgress: handleAnalyzeProgress,
    })
      .then(analyzedPlan => {
        setPlan(analyzedPlan);
        setPhase("preview");
      })
      .catch(error => {
        console.error("Knowledge-base analysis failed:", error);
        enqueueSnackbar(`Could not analyze the knowledge base: ${error.message}`, {
          variant: "error",
        });
        setStatusText(`Analysis failed: ${error.message}`);
        setPhase("error");
      });
  };

  const copyCount = (plan?.newFiles?.length || 0) + (plan?.changedFiles?.length || 0);
  const staleCount = plan?.staleFiles?.length || 0;
  const willDoNothing = copyCount === 0 && !(prune && staleCount > 0);

  const handleApplyProgress = event => {
    if (event.total > 0) {
      setProgressValue(Math.round((event.index / event.total) * 100));
    }
    if (event.phase === "copying") {
      setStatusText(`Copying ${event.key}…`);
    } else if (event.phase === "deleting") {
      setStatusText(`Removing ${event.key}…`);
    } else if (event.phase === "done") {
      setProgressValue(100);
      setStatusText("Done.");
    }
  };

  const handleConfirm = async () => {
    setPhase("applying");
    setIsDeterminate(true);
    setProgressValue(0);
    setStatusText("Starting…");

    try {
      const runResult = await applyKnowledgeBaseSync({
        plan,
        sourceBotODM,
        targetBotODM,
        prune,
        onProgress: handleApplyProgress,
      });
      setResult(runResult);
      setPhase("done");

      const summary =
        `Created ${runResult.created}, updated ${runResult.updated}` +
        (prune ? `, deleted ${runResult.deleted}` : "");
      enqueueSnackbar(
        runResult.errors.length > 0
          ? `${summary}. ${runResult.errors.length} file(s) failed.`
          : summary,
        { variant: runResult.errors.length > 0 ? "warning" : "success" },
      );
    } catch (error) {
      console.error("Knowledge-base sync failed:", error);
      enqueueSnackbar(`Sync failed: ${error.message}`, { variant: "error" });
      setStatusText(`Sync failed: ${error.message}`);
      setPhase("error");
    }
  };

  const onCloseHandler = () => {
    if (phase === "applying") {
      return;
    }
    onClose();
  };

  const busy = phase === "loading-kbs" || phase === "analyzing" || phase === "applying";

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogMessage>
          Copies one knowledge base from <strong>{sourceName}</strong> into{" "}
          <strong>{targetName}</strong>. This only ever writes Prod → Test — nothing is changed in{" "}
          {sourceName}, and only knowledge-base files are touched (never system files).
        </DialogMessage>

        {busy && (
          <Box sx={{ marginTop: theme.spacing(1) }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
              <CircularProgress size={18} color="secondary" />
              <Typography variant="body2" color="textSecondary">
                {statusText}
              </Typography>
            </Box>
            <LinearProgress
              color="secondary"
              variant={isDeterminate ? "determinate" : "indeterminate"}
              value={progressValue}
              sx={{ marginTop: theme.spacing(1), borderRadius: theme.spacing(0.5) }}
            />
          </Box>
        )}

        {phase === "error" && (
          <Typography variant="body2" color="error" sx={{ marginTop: theme.spacing(2) }}>
            {statusText}
          </Typography>
        )}

        {phase === "select" && kbData && (
          <Box sx={{ marginTop: theme.spacing(1) }}>
            <OmniTextField
              label={`Knowledge base in ${sourceName}`}
              select
              variant="outlined"
              id="sourceKb"
              value={sourceKbId}
              onChange={event => {
                const nextSource = event.target.value;
                setSourceKbId(nextSource);
                setTargetKbId(pickDefaultTargetKb(nextSource, sourceKbs, targetKbs));
              }}
            >
              {sourceKbs.map(kb => (
                <OmniMenuItem key={kb.kbId} value={kb.kbId}>
                  {`${kb.label} — ${kb.kbId} (${kb.fileCount})`}
                </OmniMenuItem>
              ))}
            </OmniTextField>

            <OmniTextField
              label={`Copy into this knowledge base in ${targetName}`}
              select
              variant="outlined"
              id="targetKb"
              value={targetKbId}
              onChange={event => setTargetKbId(event.target.value)}
              sx={{ marginTop: theme.spacing(2) }}
            >
              {targetOptions.map(kb => (
                <OmniMenuItem key={kb.kbId} value={kb.kbId}>
                  {`${kb.label} — ${kb.kbId}${kb.fileCount ? ` (${kb.fileCount})` : ""}`}
                </OmniMenuItem>
              ))}
            </OmniTextField>

            {!targetKbExists && (
              <Box
                sx={{
                  marginTop: theme.spacing(2),
                  padding: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  backgroundColor: alpha(theme.palette.warning.main, 0.15),
                  border: `1px solid ${theme.palette.warning.main}`,
                }}
              >
                <Typography variant="body2">
                  <strong>Heads up:</strong> "{targetKbId}" does not exist in {targetName} yet. Files
                  will be created under this KB id, but they won't appear until a knowledge base with
                  that id exists in {targetName}'s Studio.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {phase === "preview" && plan && (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ marginTop: theme.spacing(1) }}>
              {sourceName} · {plan.sourceKbId} → {targetName} · {plan.targetKbId}
            </Typography>

            {willDoNothing ? (
              <Typography variant="body1" sx={{ marginTop: theme.spacing(2) }}>
                This knowledge base is already in sync. Nothing to copy.
              </Typography>
            ) : (
              <>
                <FileKeySection
                  title="New files (will be created)"
                  files={plan.newFiles}
                  color={theme.palette.success.main}
                />
                <FileKeySection
                  title="Changed files (will be overwritten)"
                  files={plan.changedFiles}
                  color={theme.palette.warning.main}
                />
                {plan.unchangedCount > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ marginTop: theme.spacing(2) }}
                  >
                    {plan.unchangedCount} file(s) already in sync — skipped.
                  </Typography>
                )}
              </>
            )}

            {staleCount > 0 && (
              <>
                <Divider sx={{ marginTop: theme.spacing(2) }} />
                <FormControlLabel
                  sx={{ marginTop: theme.spacing(1) }}
                  control={
                    <Checkbox
                      color="secondary"
                      checked={prune}
                      onChange={() => setPrune(prev => !prev)}
                      name="prune"
                    />
                  }
                  label={`Delete ${staleCount} file(s) in the ${targetName} KB that are not in ${sourceName}`}
                />
              </>
            )}
          </Box>
        )}

        {phase === "done" && result && (
          <Box sx={{ marginTop: theme.spacing(2) }}>
            <Typography variant="body1">
              Created {result.created}, updated {result.updated}
              {prune ? `, deleted ${result.deleted}` : ""}.
            </Typography>
            {result.errors.length > 0 && (
              <Box sx={{ marginTop: theme.spacing(1) }}>
                <Typography variant="subtitle2" color="error">
                  {result.errors.length} file(s) failed:
                </Typography>
                <Box
                  sx={{
                    marginTop: theme.spacing(0.5),
                    padding: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    border: `1px solid ${theme.palette.divider}`,
                    maxHeight: "22vh",
                    overflowY: "auto",
                  }}
                >
                  {result.errors.map(err => (
                    <Typography key={err.key} variant="body2" sx={{ wordBreak: "break-all" }}>
                      {err.key}: {err.message}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCloseHandler}
          color="secondary"
          variant="contained"
          disabled={phase === "applying"}
        >
          {phase === "done" ? "Close" : "Cancel"}
        </Button>
        {phase === "select" && (
          <Button
            onClick={handleAnalyze}
            color="secondary"
            variant="contained"
            disabled={!sourceKbId || !targetKbId}
          >
            Analyze changes
          </Button>
        )}
        {phase === "preview" && (
          <Button
            onClick={handleConfirm}
            color="secondary"
            variant="contained"
            disabled={willDoNothing}
          >
            {prune && staleCount > 0
              ? `Copy ${copyCount} & delete ${staleCount}`
              : `Copy ${copyCount} file(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BotpressKnowledgeBaseSyncDialog;
